import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { userRoles } from 'utils/currentUserRoles';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';

// Components
import MyAuthorizations from 'feature/loading-management/my-authorizations/MyAuthorizations.connector';
import CarrierAuthorizations from 'feature/loading-management/carrier-authorizations/CarrierAuthorizations.connector';
import SelfAuthorizations from 'feature/loading-management/self-authorizations/SelfAuthorizations.connector';
import SelfAuthorizationsListViewPage from 'feature/loading-management/self-authorizations/self-authorizations-grid/SelfAuthorizationsListViewPage';
import SupplierAuthorizations from 'feature/loading-management/supplier-authorizations/SupplierAuthorizations.connector';
import AuthorizationSequencingOld from 'feature/loading-management/authorization-sequencing/AuthorizationSequencing.connector';
import AuthorizationSequencing from 'feature/loading-management/authorization-sequencing/AuthorizationSequencing';
import { hasPersistedAuthSeqFilters } from 'feature/loading-management/authorization-sequencing/AuthorizationSequencingOld';
import { isAuthSequencingUpdatesEnabled } from 'feature/loading-management/utils/const';
import LoadingDashboard from 'feature/loading-management/dashboard/Dashboard';
import CustomerAuthorizations from 'feature/loading-management/customer-authorizations/CustomerAuthorizationsConnector';
import RackWaitTimes from 'feature/loading-management/terminal-information/rack-wait-times/RackWaitTimes.connector';
import TerminalDetails from 'feature/loading-management/terminal-information/terminal-details/TerminalDetails';
import TerminalLocator from 'feature/loading-management/terminal-information/terminal-locator/TerminalLocator';

// Create Flows
import CreateCustomerAuth from 'feature/loading-management/customer-authorizations/create/CreateConnector';
import CustomerInformation from 'feature/loading-management/customer-authorizations/create/customer-information/CustomerInformation';
import TerminalsProducts from 'feature/loading-management/customer-authorizations/create/terminals-products/TerminalsProducts';
import DestinationsControlNumbers from 'feature/loading-management/customer-authorizations/create/loading-control-numbers/DestinationsControlNumbers';
import Carriers from 'feature/loading-management/customer-authorizations/create/carriers/Carriers';
import Summary from 'feature/loading-management/customer-authorizations/create/summary/Summary';
import TerminalAllocationLoadingPercentage from 'feature/loading-management/terminal-allocation-loading/TerminalAllocationLoadingPercentage';

// Utils
import { useGetAffiliateOptions, useRedirectAffiliateForRole } from 'hooks';

const getPersistentAuthSeqView = () => {
  try {
    const stored = localStorage.getItem('authorization-sequencing.view');
    return stored === 'location' || stored === 'product' ? stored : 'product';
  } catch {
    return 'product';
  }
};

const hasAuthSeqFiltersForView = view => {
  try {
    const stored = localStorage.getItem(`authorization-sequencing.filters.${view}`);
    if (!stored) {
      return false;
    }

    const persistedFilters = JSON.parse(stored);
    return hasPersistedAuthSeqFilters(persistedFilters);
  } catch {
    return false;
  }
};

const AuthSeqRedirect = () => {
  // Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
  if (!isAuthSequencingUpdatesEnabled()) {
    return <Navigate to="./location" />;
  }

  const hasLocationFilters = hasAuthSeqFiltersForView('location');
  const hasProductFilters = hasAuthSeqFiltersForView('product');

  if (hasLocationFilters && !hasProductFilters) {
    return <Navigate to="./location" />;
  }

  if (hasLocationFilters && hasProductFilters) {
    return <Navigate to={`./${getPersistentAuthSeqView()}`} />;
  }

  return <Navigate to="./product" />;
};

const LoadingManagement = () => {
  const { pathname } = useLocation();
  const isSelfAuth = pathname.includes('self-authorizations');
  const isSupplierAuth = pathname.includes('supplier-authorizations');
  const isSelfAuthListViewEnabled = EvaluateFlag(process.env.REACT_APP_LD_SELF_AUTH_LIST_VIEW);
  const roles =
    isSelfAuth || isSupplierAuth
      ? [userRoles.SUPPLIER, userRoles.INVENTORY_OWNER]
      : [userRoles.SUPPLIER];
  useRedirectAffiliateForRole(useGetAffiliateOptions(roles));

  return (
    <>
      <Routes>
        <Route element={<LoadingDashboard />} index />
        <Route
          element={<Navigate to={isSelfAuthListViewEnabled ? './list-view' : './location'} />}
          path="self-authorizations"
        />
        <Route
          element={
            isSelfAuthListViewEnabled ? (
              <SelfAuthorizationsListViewPage />
            ) : (
              <Navigate to="../self-authorizations/location" />
            )
          }
          path="self-authorizations/list-view"
        />
        <Route
          element={
            isSelfAuthListViewEnabled ? (
              <Navigate to="../self-authorizations/list-view" />
            ) : (
              <SelfAuthorizations />
            )
          }
          path="self-authorizations/:viewBy"
        />
        <Route
          element={
            isSelfAuthListViewEnabled ? (
              <Navigate to="../self-authorizations/list-view" />
            ) : (
              <SelfAuthorizations />
            )
          }
          path="self-authorizations/:viewBy/:viewById"
        />
        <Route element={<Navigate to="./supplier" />} path="supplier-authorizations" />
        <Route element={<SupplierAuthorizations />} path="supplier-authorizations/:viewBy" />
        <Route
          element={<SupplierAuthorizations />}
          path="supplier-authorizations/:viewBy/:viewById"
        />
        {EvaluateFlag(process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_FILTERABLE_CONTENT_GRID) ? (
          <>
            <Route element={<AuthorizationSequencing />} path="authorization-sequencing/*" />
          </>
        ) : (
          <>
            <Route element={<AuthSeqRedirect />} path="authorization-sequencing" />
            <Route
              element={<AuthorizationSequencingOld />}
              path="authorization-sequencing/:viewBy"
            />
            <Route
              element={<AuthorizationSequencingOld />}
              path="authorization-sequencing/:viewBy/:viewById"
            />
          </>
        )}
        <Route element={<Navigate to="./carrier" />} path="carrier-authorizations" />
        <Route element={<CarrierAuthorizations />} path="carrier-authorizations/:viewBy" />
        <Route
          element={<CarrierAuthorizations />}
          path="carrier-authorizations/:viewBy/:viewById"
        />
        <Route path="customer-authorizations">
          <Route element={<CustomerAuthorizations />} index />
          <Route element={<CreateCustomerAuth />} path="create">
            <Route element={<CustomerInformation />} path="customer-information" />
            <Route element={<TerminalsProducts />} path="terminals-products" />
            <Route element={<DestinationsControlNumbers />} path="loading-control-numbers" />
            <Route element={<Carriers />} path="carriers" />
            <Route element={<Summary />} path="summary" />
            <Route element={<Navigate to="./customer-information" />} path="*" />
          </Route>
        </Route>
        <Route element={<Navigate to="./rack-wait-times" />} path="terminal-information" />
        <Route path="terminal-information">
          <Route element={<RackWaitTimes />} path="rack-wait-times" />
          <Route element={<RackWaitTimes />} path="rack-wait-times/:viewById" />
          <Route element={<TerminalLocator />} path="terminal-locator" />
          <Route element={<TerminalDetails />} path="terminal-locator/:terminalId" />
        </Route>
        <Route
          element={<TerminalAllocationLoadingPercentage />}
          path="terminal-allocation-loading-percentage"
        />
        <Route element={<Navigate to="./inventory-owner" />} path="my-authorizations" />
        <Route element={<MyAuthorizations />} path="my-authorizations/:viewBy" />
        <Route element={<MyAuthorizations />} path="my-authorizations/:viewBy/:viewById" />
      </Routes>
    </>
  );
};

export default LoadingManagement;
